import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Camera, ShieldAlert, Bell, Cable, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

// Same 4 categories used in the header's mega-menu, so search results and
// browse-by-category stay in sync.
const GROUPS = [
  { v: "camera", label: "CCTV Cameras", icon: Camera },
  { v: "alarm", label: "Alarm Systems", icon: ShieldAlert },
  { v: "intercom", label: "Intercoms", icon: Bell },
  { v: "accessory,dvr", label: "Accessories", icon: Cable },
];

export default function HeaderSearch({ dark }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const runSearch = useCallback((value) => {
    if (!value.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    api.get("/products", { params: { q: value, limit: 40 } })
      .then(r => setResults(Array.isArray(r.data) ? r.data : []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (e) => {
    const value = e.target.value;
    setQ(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const grouped = GROUPS.map(g => {
    const vals = g.v.split(",");
    return { ...g, items: results.filter(r => vals.includes(r.category)).slice(0, 3) };
  }).filter(g => g.items.length > 0);

  const totalCount = results.length;

  const goToProduct = (id) => { setOpen(false); setQ(""); setResults([]); nav(`/products/${id}`); };
  const seeAll = () => { setOpen(false); nav(`/products?q=${encodeURIComponent(q)}`); };

  const chromeClass = dark
    ? "border-white/20 bg-white/5 text-white placeholder:text-white/40"
    : "border-border bg-secondary/40 text-foreground placeholder:text-muted-foreground";

  return (
    <div ref={wrapRef} className="relative">
      <motion.div
        initial={false}
        animate={{ width: open ? 320 : 200 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className={`hidden sm:flex items-center gap-2 h-10 rounded-full border px-3.5 cursor-text transition-colors ${chromeClass} ${open ? "ring-2 ring-eagle-gold/40" : ""}`}
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        data-testid="header-search-trigger"
      >
        {loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-eagle-gold" /> : <Search className="h-4 w-4 shrink-0 opacity-60" />}
        <input
          ref={inputRef}
          value={q}
          onChange={onChange}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) seeAll(); }}
          placeholder="Search cameras, alarms, intercoms…"
          className="w-full bg-transparent text-sm outline-none placeholder:truncate"
          data-testid="header-search-input"
        />
        {q && (
          <button
            onClick={(e) => { e.stopPropagation(); setQ(""); setResults([]); inputRef.current?.focus(); }}
            className="shrink-0 opacity-60 hover:opacity-100"
            aria-label="Clear search"
            data-testid="header-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </motion.div>

      {/* Mobile trigger: just an icon button that opens the same panel */}
      <button
        onClick={() => { setOpen(v => !v); setTimeout(() => inputRef.current?.focus(), 50); }}
        className={`sm:hidden h-11 w-11 rounded-full border flex items-center justify-center ${dark ? "border-white/20" : "border-border"}`}
        aria-label="Search"
        data-testid="header-search-mobile-trigger"
      >
        <Search className="h-4 w-4" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute right-0 sm:right-auto sm:left-0 top-full mt-2 w-[92vw] max-w-sm sm:w-[420px] bg-popover text-popover-foreground border border-border rounded-2xl shadow-2xl overflow-hidden z-50"
            data-testid="header-search-panel"
          >
            {!q.trim() ? (
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">Browse by category</div>
                <div className="grid grid-cols-2 gap-2">
                  {GROUPS.map(g => (
                    <button
                      key={g.v}
                      onClick={() => { setOpen(false); nav(`/products?category=${g.v}`); }}
                      className="flex items-center gap-2 p-2.5 rounded-lg border border-border/70 hover:border-eagle-gold hover:bg-secondary transition-colors text-left"
                      data-testid={`search-quick-cat-${g.v}`}
                    >
                      <g.icon className="h-4 w-4 text-eagle-gold shrink-0" strokeWidth={1.75} />
                      <span className="text-xs font-medium truncate">{g.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : loading ? (
              <div className="p-8 flex items-center justify-center text-muted-foreground text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            ) : totalCount === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No matches for &ldquo;{q}&rdquo;. Try a brand, category or SKU.
              </div>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
                {grouped.map((g, gi) => (
                  <motion.div
                    key={g.v}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: gi * 0.05 }}
                    className="p-4"
                  >
                    <div className="flex items-center gap-2 mb-2.5 text-eagle-gold">
                      <g.icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">{g.label}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      {g.items.map(p => (
                        <button
                          key={p.product_id}
                          onClick={() => goToProduct(p.product_id)}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors text-left"
                          data-testid={`search-result-${p.product_id}`}
                        >
                          <div className="h-9 w-9 rounded-md bg-secondary overflow-hidden shrink-0">
                            {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{p.brand_name}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ))}
                <button
                  onClick={seeAll}
                  className="w-full p-3.5 flex items-center justify-center gap-2 text-sm font-semibold text-eagle-gold hover:bg-secondary transition-colors"
                  data-testid="search-see-all"
                >
                  See all {totalCount} results <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}